import { Box, Flex } from "@zenchef/styled-system/jsx";
import React, {
  ElementRef,
  forwardRef,
  PropsWithChildren,
  RefAttributes,
} from "react";
import {
  aromaListItem,
  AromaListItemVariantProps,
} from "@zenchef/styled-system/recipes";
import { cx } from "@zenchef/styled-system/css";

interface ListItemChildProps
  extends PropsWithChildren,
    AromaListItemVariantProps {
  actions?: React.ReactNode[];
  className?: string;
}

const ListItemChild = forwardRef<HTMLDivElement, ListItemChildProps>(
  ({ children, actions, className, ...propsWithVariants }, ref) => {
    const [variants] = aromaListItem.splitVariantProps({
      ...propsWithVariants,
    });
    const classes = aromaListItem(variants);

    return (
      <Box ref={ref} className={cx(classes.item, className)}>
        <Box className={classes.itemContent}>{children}</Box>
        {actions?.length ? (
          <Box className={classes.itemActions}>
            {actions.map((action, index) => (
              <React.Fragment key={`action-${index}`}>{action}</React.Fragment>
            ))}
          </Box>
        ) : null}
      </Box>
    );
  },
);

interface ListItemRootProps extends React.PropsWithChildren {
  className?: string;
}

const ListItemRoot = forwardRef<HTMLDivElement, ListItemRootProps>(
  ({ children, className, ...propsWithVariants }, ref) => {
    const [variants] = aromaListItem.splitVariantProps({
      ...propsWithVariants,
    });
    const classes = aromaListItem(variants);

    return (
      <Flex ref={ref} className={cx(classes.root, className)}>
        {children}
      </Flex>
    );
  },
);

interface ListItemProps
  extends Omit<ListItemRootProps, "children">,
    AromaListItemVariantProps {
  items: ListItemChildProps[];
  itemClassName?: string;
}

const ListItemContainer = forwardRef<HTMLDivElement, ListItemProps>(
  ({ items, className, itemClassName, ...propsWithVariants }, ref) => {
    const [variants] = aromaListItem.splitVariantProps({
      ...propsWithVariants,
    });
    return (
      <ListItemRoot ref={ref} className={className}>
        {items.map((item, index) => (
          <ListItemChild
            key={`item-${index}`}
            className={itemClassName}
            {...item}
            {...variants}
          />
        ))}
      </ListItemRoot>
    );
  },
) as (
  props: ListItemProps & RefAttributes<ElementRef<"div">>,
) => React.ReactElement;

export type { ListItemProps, ListItemRootProps, ListItemChildProps };
export default Object.assign(ListItemContainer, {
  Root: ListItemRoot,
  Item: ListItemChild,
});
